import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';

import Button from '../../../components/Buttons/Button';
import { api } from '../../../sagas';
import { TRAVEL_MAX_DAYS, WATCHDOG_COPY } from '../NewsReport/watchdogCopy';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './TravelDatesDialog.module.scss';

type TravelDatesDialogType = {
	open: boolean;
	travelReportId: string;
	initialStartDate?: string | null;
	initialEndDate?: string | null;
	onClose: () => void;
	onSubmitted: () => void;
};

const DATE_FORMAT = 'YYYY-MM-DD';
/** How far ahead a trip may start; its length is capped by TRAVEL_MAX_DAYS. */
const MAX_YEARS_AHEAD = 2;
const PORTAL_ID = 'travel-dates-datepicker-portal';

function toDate(value?: string | null): Date | null {
	if (!value) {
		return null;
	}
	const parsed = moment(value);
	return parsed.isValid() ? parsed.startOf('day').toDate() : null;
}

/** Last selectable end date: the range is inclusive, so start + (max - 1) days. */
function maxTripEnd(start: Date): Date {
	return moment(start)
		.add(TRAVEL_MAX_DAYS - 1, 'days')
		.toDate();
}

export default function TravelDatesDialog(props: TravelDatesDialogType) {
	const { open, travelReportId, initialStartDate, initialEndDate, onClose, onSubmitted } = props;

	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(false);

	// The calendar renders into a body-level portal so the modal's overflow can't
	// clip the month header and navigation arrows (§10.2).
	useEffect(() => {
		if (document.getElementById(PORTAL_ID)) {
			return;
		}
		const node = document.createElement('div');
		node.id = PORTAL_ID;
		document.body.appendChild(node);
	}, []);

	useEffect(() => {
		if (open) {
			setStartDate(toDate(initialStartDate));
			setEndDate(toDate(initialEndDate));
			setSubmitError(false);
			setSubmitting(false);
		}
	}, [open, initialStartDate, initialEndDate]);

	const today = moment().startOf('day').toDate();
	const horizon = moment().add(MAX_YEARS_AHEAD, 'years').endOf('day').toDate();

	const endMax = startDate && maxTripEnd(startDate) < horizon ? maxTripEnd(startDate) : horizon;

	const isValidRange =
		!!startDate &&
		!!endDate &&
		moment(endDate).isSameOrAfter(moment(startDate), 'day') &&
		moment(endDate).isSameOrBefore(moment(endMax), 'day');

	const resetState = () => {
		setStartDate(null);
		setEndDate(null);
		setSubmitError(false);
		setSubmitting(false);
	};

	const handleCancel = () => {
		if (submitting) {
			return;
		}
		resetState();
		onClose();
	};

	const handleSubmit = () => {
		if (!startDate || !endDate) {
			return;
		}

		setSubmitting(true);
		setSubmitError(false);

		api
			.setHomeDestinationReportTravelPeriod({
				travelReportId,
				fromDate: `${moment(startDate).format(DATE_FORMAT)}T00:00:00`,
				toDate: `${moment(endDate).format(DATE_FORMAT)}T00:00:00`,
			})
			.then((response: { ok: boolean }) => {
				if (response.ok) {
					resetState();
					onSubmitted();
				} else {
					setSubmitting(false);
					setSubmitError(true);
				}
			})
			.catch(() => {
				setSubmitting(false);
				setSubmitError(true);
			});
	};

	const sharedPickerProps = {
		dateFormat: 'MMM d, yyyy',
		portalId: PORTAL_ID,
		showMonthDropdown: true,
		showYearDropdown: true,
		dropdownMode: 'select' as const,
		className: styles.input,
		wrapperClassName: styles.datePickerWrapper,
		popperClassName: styles.datePickerPopper,
		showPopperArrow: false,
		showIcon: true,
		calendarIconClassName: styles.calendarIcon,
	};

	return (
		<Dialog
			onClose={handleCancel}
			aria-labelledby="travel-dates-dialog-title"
			open={open}
			classes={{ paper: styles.paper }}
		>
			<MuiDialogTitle disableTypography className={styles.title}>
				<Typography variant="h4" className={styles.titleText} id="travel-dates-dialog-title">
					{WATCHDOG_COPY.datesDialogTitle}
				</Typography>
			</MuiDialogTitle>
			<MuiDialogContent className={styles.dialogContent}>
				<p className={styles.description}>{WATCHDOG_COPY.datesDialogHelper}</p>
				<div className={styles.dialogFields}>
					<label htmlFor="travel-dates-start-date" className={styles.field}>
						{WATCHDOG_COPY.datesDialogStartLabel}
						<div className={styles.inputWrapper}>
							<DatePicker
								id="travel-dates-start-date"
								selected={startDate}
								onChange={(date: Date | null) => {
									const next = date ? moment(date).startOf('day').toDate() : null;
									setStartDate(next);
									if (
										next &&
										endDate &&
										(moment(endDate).isBefore(next, 'day') ||
											moment(endDate).isAfter(maxTripEnd(next), 'day'))
									) {
										setEndDate(null);
									}
								}}
								selectsStart
								startDate={startDate}
								endDate={endDate}
								minDate={today}
								maxDate={horizon}
								placeholderText="Select start date"
								{...sharedPickerProps}
							/>
						</div>
					</label>
					<label htmlFor="travel-dates-end-date" className={styles.field}>
						{WATCHDOG_COPY.datesDialogEndLabel}
						<div className={styles.inputWrapper}>
							<DatePicker
								id="travel-dates-end-date"
								selected={endDate}
								onChange={(date: Date | null) =>
									setEndDate(date ? moment(date).startOf('day').toDate() : null)
								}
								selectsEnd
								startDate={startDate}
								endDate={endDate}
								minDate={startDate || today}
								maxDate={endMax}
								placeholderText="Select end date"
								{...sharedPickerProps}
							/>
						</div>
					</label>
				</div>
				<p className={styles.rangeHint}>{WATCHDOG_COPY.datesDialogRangeHint}</p>
				{submitError && <p className={styles.dialogError}>{WATCHDOG_COPY.datesDialogError}</p>}
			</MuiDialogContent>
			<MuiDialogActions className={styles.actions}>
				<Button
					className={styles.cancelButton}
					variant="text"
					color="primary"
					disabled={submitting}
					onClick={handleCancel}
				>
					{WATCHDOG_COPY.datesDialogCancel}
				</Button>
				<Button
					variant="filled"
					color="primary"
					className={styles.submitButton}
					disabled={!isValidRange || submitting}
					onClick={handleSubmit}
				>
					{WATCHDOG_COPY.datesDialogSave}
				</Button>
			</MuiDialogActions>
		</Dialog>
	);
}
